import { useMutation, useReactiveVar } from '@apollo/client';
import { ButtonV2, Card } from '@vartanainc/design-system';
import { Form, FormikProvider, useFormik } from 'formik';
import { get } from 'lodash';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import AutoLoad from '../../components/AutoLoad';
import CustomNotification from '../../components/CustomNotification';
import SalesRepField from '../../components/SalesRepSelect';
import SvgIcon from '../../components/SvgIcon/SvgIcon';
import { ROLES } from '../../constants/common.constants';
import { TEXT_CONSTANTS } from '../../constants/ui/text.constants';
import TextInputField from '../../designSystem/TextInput/TextInputField';
import { assignToMyTeamValidationSchema } from '../../formikSchema/commonSchema';
import { sessionVar } from '../../graphql/cache';
import { UPDATE_USER } from '../../graphql/queries/user';

interface User {
  id: string;
  fullName: string;
  firstName: string;
  lastName: string;
  userRole?: {
    id: string;
    name: string;
  };
  managedUsers: User[];
}

interface SessionData {
  session?: {
    user: User;
  };
}

interface FormValues {
  fullName: string;
  users: User[];
}

interface AssignToMyTeamProps {
  onCancel: () => void;
  salesManager?: User;
}

const AssignToMyTeam = ({ onCancel, salesManager }: AssignToMyTeamProps): JSX.Element => {
  const sessionData: SessionData = useReactiveVar(sessionVar);
  const currentUser = get(sessionData, 'session.user') as User;
  const currentUserRole = currentUser?.userRole?.name ?? '';
  const [upsertUser] = useMutation(UPDATE_USER);
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);
  const navigate = useNavigate();

  const userHasSalesReps = useMemo(() => {
    return (
      currentUserRole === ROLES.SALES_MANAGER && currentUser?.managedUsers?.length > 0
    );
  }, [currentUser, currentUserRole]);

  const initialFormValues: FormValues = useMemo(
    () => ({
      fullName: (salesManager?.fullName || currentUser?.fullName) ?? '',
      users: [],
    }),
    [currentUser, salesManager]
  );

  const handleCancelButtonClicked = (): void => {
    if (userHasSalesReps) {
      onCancel();
    } else {
      navigate(-1);
    }
  };

  const getSuccessMessage = (users: User[]): string => {
    const isSingleUser = users.length === 1;
    if (salesManager) {
      const name = `${salesManager.firstName} ${salesManager.lastName}`;
      return isSingleUser
        ? `${TEXT_CONSTANTS.SALES_REP_ASSIGNED_SINGLE} ${name}.`
        : `${TEXT_CONSTANTS.SALES_REP_ASSIGNED_MULTIPLE} ${name}.`;
    }
    return isSingleUser
      ? TEXT_CONSTANTS.SUCCESS_MESSAGE_FOR_SINGLE_SALES_REP
      : TEXT_CONSTANTS.SUCCESS_MESSAGE_FOR_MULTIPLE_SALES_REP;
  };

  const handleSubmit = async (
    values: FormValues,
    { setErrors }: { setErrors: (errors: { [key: string]: string }) => void }
  ): Promise<void> => {
    setIsFormSubmitting(true);

    try {
      if (values.users.length > 0 && currentUser) {
        const record = {
          id: salesManager ? +salesManager?.id : +currentUser.id,
          roleId: +(salesManager?.userRole?.id || currentUser.userRole?.id || 0),
          users: values.users.map((user) => +user.id),
        };

        await upsertUser({ variables: record });

        if (!userHasSalesReps) {
          const getSession = get(sessionData, 'getSessionData', () => null);
          getSession();
        }

        toast.success(({ toastProps }) => (
          <CustomNotification
            type={toastProps.type || 'success'}
            message={getSuccessMessage(values.users)}
          />
        ));
        onCancel();
      }
    } catch (error) {
      setErrors({ general: (error as Error).message });
    } finally {
      setIsFormSubmitting(false);
    }
  };

  const formikBag = useFormik({
    initialValues: initialFormValues,
    validationSchema: assignToMyTeamValidationSchema,
    onSubmit: handleSubmit,
    enableReinitialize: true,
  });

  return (
    <div className="p-10">
      <FormikProvider value={formikBag}>
        <Form className="create-user-container">
          <Card
            tabindex={0}
            title="Assign sales rep"
            icon={(
              <SvgIcon
                name="group_add"
                width="2rem"
                height="2rem"
                fill="color-blue-120"
              />
            )}
            content={(
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <TextInputField
                      {...{
                        name: 'fullName',
                        label: 'Sales Manager',
                        fullWidth: 'true',
                        disabled: true,
                      }}
                      ref={null}
                    />
                  </div>
                </div>

                <SalesRepField
                  name="users"
                  label={TEXT_CONSTANTS.ASSIGN_SALES_REP}
                  placeholder={TEXT_CONSTANTS.ASSIGN_SALES_REP_PLACEHOLDER}
                />

                <div className="flex flex-row justify-between mt-3">
                  <ButtonV2
                    type="button"
                    text="Cancel"
                    iconLeft="chevron_left"
                    variant={{ type: 'ghost', typography: 'paragraph14' }}
                    onClick={handleCancelButtonClicked}
                  />

                  <AutoLoad loading={isFormSubmitting}>
                    <ButtonV2
                      type="submit"
                      disabled={!formikBag.isValid || !formikBag.dirty}
                      text="Submit"
                      variant={{ type: 'primary', typography: 'paragraph14' }}
                    />
                  </AutoLoad>
                </div>
              </>
            )}
          />
        </Form>
      </FormikProvider>
    </div>
  );
};

export default AssignToMyTeam;
